import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, take, takeUntil, timeout } from 'rxjs/operators';
import { ElectionService } from 'src/app/services/election.service';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { RDColumn } from 'src/app/models/RDTable';
import { MatSort } from '@angular/material/sort';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { IReportFirstPreference } from 'src/app/interfaces/IReportFirstPreference';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { ITableHeader } from 'src/app/interfaces/ITableHeader';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';

@Component({
  selector: 'first-preference-table',
  templateUrl: './first-preference-table.component.html',
  styleUrls: ['./first-preference-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class FirstPreferenceTableComponent implements AfterViewInit, OnDestroy {
  @Input() isSmallTable = true;
  @Input() title = 'First Preference Votes';

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public firstPreferences: IReportFirstPreference[] = [];
  public independentDetails: IReportFirstPreference[] = [];
  public isIndependentsExpanded = false;

  public dataTable: IReportFirstPreference[] = [];
  public dataSource = new MatTableDataSource<IReportFirstPreference>();
  public columns: ITableHeader[] = [];
  public displayedColumns: string[] = [];
  public dataIsLoaded = true;

  constructor(private electionService: ElectionService) {
    this.electionService.dataLoaded$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((dataLoaded) => {
        if (this.isDataLoaded(dataLoaded.electionDate)) {
          this.loadChart(this.electionService.selectedPage);
        }
      });
    this.electionService.pageChanged$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((page) => {
        this.loadChart(page);
      });
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic,
      ]);
    }
    return false;
  }

  ngAfterViewInit(): void {
    if (
      this.isDataLoaded(
        this.electionService.selectedPage.electionDateString ?? ''
      )
    ) {
      this.loadChart(this.electionService.selectedPage);
    }
  }

  // Helper method to get column value from element
  getColumnValue(element: IReportFirstPreference, columnName: string): any {
    return (element as any)[columnName];
  }

  private loadChart(page: ISelectPage): void {
    this.columns = [];
    this.isIndependentsExpanded = false; // Reset expansion state when page changes
    const election = this.electionService.electionMap.get(
      page.electionDateString ?? ''
    );
    if (page.pageType === PageTypeEnum.CountHAStateWide) {
      const allParties = election?.electionCount?.parties ?? [];

      // Separate parties by type
      const { independents, others } = allParties.reduce(
        (acc, party) => {
          if (party.partyId === 'IND') {
            acc.independents.push(party);
          } else {
            acc.others.push(party);
          }
          return acc;
        },
        {
          independents: [] as typeof allParties,
          others: [] as typeof allParties,
        }
      );

      // Map non-independent parties
      const nonIndependentEntries = others.map((party) => {
        return {
          district: null,
          partyId: party.partyId,
          party: `${party.partyName} (${party.partyId})`,
          colourCode: party.colourCode,
          candidateName: null,
          votes: party.firstPrefVotes,
          share: party.firstPrefShare,
          change: party.firstPrefChange,
        };
      });

      // Store individual independent entries for expansion
      this.independentDetails = independents.map((party) => {
        return {
          district: null,
          partyId: party.partyId,
          party: `${party.partyName} (${party.partyId})`,
          colourCode: party.colourCode,
          candidateName: null,
          votes: party.firstPrefVotes,
          share: party.firstPrefShare,
          change: party.firstPrefChange,
        };
      });

      // Calculate total votes for share calculation
      const totalVotes = allParties.reduce(
        (sum, party) => sum + party.firstPrefVotes,
        0
      );

      // Create grouped independents entry if any exist
      const independentEntry =
        independents.length > 0
          ? [
              {
                district: null,
                partyId: 'IND',
                party: 'Independents',
                colourCode: independents[0].colourCode,
                candidateName: null,
                votes: independents.reduce(
                  (sum, party) => sum + party.firstPrefVotes,
                  0
                ),
                share:
                  totalVotes > 0
                    ? (independents.reduce(
                        (sum, party) => sum + party.firstPrefVotes,
                        0
                      ) /
                        totalVotes) *
                      100
                    : 0,
                change: null,
              },
            ]
          : [];

      // Combine entries
      this.firstPreferences = [...nonIndependentEntries, ...independentEntry];
      this.firstPreferences.sort((a, b) => b.votes - a.votes);
      this.columns.push({
        column: 'colourCode',
        label: '',
        labelTwo: '',
        isTwoRowLabel: false,
        minWidth: 20,
        isBold: false,
        isRightJustify: false,
        isPercent: false,
        isColour: true,
        isColourBar: false,
        colourCode: null,
      });
    }
    if (page.pageType === PageTypeEnum.CountHADistrict) {
      const foundDistrict = election?.electionCount?.getDistrict(
        page.countDistrict
      );
      if (foundDistrict) {
        this.firstPreferences = foundDistrict.candidates.map((candidate) => {
          return {
            district: foundDistrict.districtName,
            partyId: candidate.partyId,
            party: `${candidate.partyName} (${candidate.partyId})`,
            colourCode: candidate.colourCode,
            candidateName: `${candidate.candidateName} ${
              candidate.wasPreviousMember ? '(M)' : ''
            }`,
            votes: candidate.ordinaryVoteCount + candidate.declarationVoteCount,
            share: candidate.firstPrefShare,
            change: candidate.candidateId, // todo: this needs to be fixed
          };
        });
      }
      this.firstPreferences.sort((a, b) => (a.change ?? 0) - (b.change ?? 0));
      this.columns.push({
        column: 'colourCode',
        label: '',
        labelTwo: '',
        isTwoRowLabel: false,
        minWidth: 20,
        isBold: false,
        isRightJustify: false,
        isPercent: false,
        isColour: true,
        isColourBar: false,
        colourCode: null,
      });
      this.columns.push({
        column: 'candidateName',
        label: 'Candidate',
        labelTwo: '',
        isTwoRowLabel: false,
        minWidth: 100,
        isBold: false,
        isRightJustify: false,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null,
      });
    }

    this.columns.push({
      column: 'party',
      label: 'Abbreviation',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 60,
      isBold: false,
      isRightJustify: false,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null,
    });
    this.columns.push({
      column: 'votes',
      label: 'Votes',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null,
    });
    this.columns.push({
      column: 'share',
      label: 'Share',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: true,
      isColour: false,
      isColourBar: true,
      colourCode: null,
    });
    // if (page.pageType === PageTypeEnum.CountHADistrict || page.pageType === PageTypeEnum.CountHAStateWide) {
    //   this.columns.push({
    //     column: 'change',
    //     label: 'Change',
    //     labelTwo: '',
    //     isTwoRowLabel: false,
    //     minWidth: 60,
    //     isBold: false,
    //     isRightJustify: true,
    //     isPercent: true,
    //     isColour: false,
    //     isColourBar: false
    //   });
    // }
    this.dataTable = this.firstPreferences;
    // Add expand column only for statewide page (where independents are grouped)
    if (page.pageType === PageTypeEnum.CountHAStateWide) {
      this.displayedColumns = ['expand', ...this.columns.map((c) => c.column)];
    } else {
      this.displayedColumns = this.columns.map((c) => c.column);
    }
    this.updateDataSource();
  }

  toggleIndependents(): void {
    this.isIndependentsExpanded = !this.isIndependentsExpanded;
    this.updateDataSource();
  }

  private updateDataSource(): void {
    if (this.isIndependentsExpanded && this.independentDetails.length > 0) {
      // Find the index of the Independents row
      const independentsIndex = this.firstPreferences.findIndex(
        (p) => p.partyId === 'IND'
      );
      if (independentsIndex !== -1) {
        // Insert individual independents after the grouped row
        const updatedData = [...this.firstPreferences];
        updatedData.splice(
          independentsIndex + 1,
          0,
          ...this.independentDetails
        );
        this.dataTable = updatedData;
      } else {
        this.dataTable = this.firstPreferences;
      }
    } else {
      // Show only grouped independents
      this.dataTable = this.firstPreferences;
    }
    if (this.dataTable?.length > 0) {
      this.dataSource = new MatTableDataSource<IReportFirstPreference>(
        this.dataTable
      );
    }
  }

  isIndependentsRow(element: IReportFirstPreference): boolean {
    return element.partyId === 'IND' && element.party === 'Independents';
  }

  isIndividualIndependent(element: IReportFirstPreference): boolean {
    return (
      element.partyId === 'IND' &&
      element.party !== 'Independents' &&
      this.independentDetails.some((ind) => ind.party === element.party)
    );
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
